import gql from 'graphql-tag';

export const CORE_USER_FIELDS = gql`
  fragment CoreUserFields on User {
    email
    fullName
    notificationMode
    first_name: firstName
    id
    name: lastName
    categorizationAuto
  }
`;

export const ME_USER_FIELDS = gql`
  ${CORE_USER_FIELDS}
  fragment MeUserFields on User {
    access_code: roleName
    ...CoreUserFields
    authMode
    language
    roleExpand { id name }
    starting_dashboard: startingDashboard
    userLastVersionViewed
    imageUrl
    firstLogin
    favorites
  }
`;

export const EXTENDED_USER_FIELDS = gql`
  ${CORE_USER_FIELDS}
  fragment ExtendedUserFields on User {
    ...CoreUserFields
    access: role
    authMode
    access_code: roleName
    activate_notification: activateNotification
    createdAt
    language
    notification_mode: notificationMode
    starting_dashboard: startingDashboard
    startingDashboard
    user_last_version_viewed: userLastVersionViewed
    roleExpand { id name }
  }
`;

export const CORE_ROLE_FIELDS = gql`
  fragment CoreRoleFields on Role {
    id
    name
  }
`;

export const CORE_GROUP_FIELDS = gql`
  fragment CoreGroupFields on Group {
    id
    name
    description
#    descriptions{
#      en
#      fr
#      de
#    }
    factory: factoryExpand{
      id
      name
    }
    machines: machinesExpand{
      id
      name
    }
  }
`;

export const CORE_MACHINE_FIELDS = gql`
  fragment CoreMachineFields on Machine {
    id
    name
    color
    shortCode
    kpiReferential
  }
`;

export const MACHINE_STATUS_FIELDS = gql`
    fragment MachineStatusFields on Machine {
        machine_state: state
        machine_status_updated_at: statusUpdatedAt
    }
`;

export const CORE_MACHINE_COUNT_TYPE_FIELDS = gql`
  fragment CoreMachineCountTypeFields on MachineCountType {
    mct_id: id
    mct_name: name
  }
`;

export const MACHINE_FACTORY_EXPAND_FIELD = gql`
  fragment MachineFactoryExpandField on Machine {
    machine_factory_expand: factoryExpand {
      factory_id:id
    }
  }
`;

export const MACHINE_COUNT_TYPE_EXPAND_FIELD = gql`
  fragment MachineCountTypeExpandField on Machine {
    machine_count_type_expand : countTypeExpand {
      mct_name: name
    }
  }
`;

export const MACHINE_DEFAULT_COUNT_TYPE_EXPAND_FIELD = gql`
  fragment MachineDefaultCountTypeExpandField on Machine {
    defaultAssociatedCountType_expand : countTypeExpand {
      mct_name: name
    }
  }
`;

export const MACHINE_DEVICES_EXPAND_FIELD = gql`
  fragment MachineDevicesExpandField on Machine {
    machine_devices: devicesExpand {
      device_id: id
    }
  }
`;

export const MACHINE_LAST_EVENT_EXPAND_FIELD = gql`
  fragment MachineLastEventExpandField on Machine {
    last_event: lastEventExpand {
      event_id: id
      event_start: start
      event_end: end
      event_type: type
      event_number: typeExpand {
        et_code: code
        color
      }
    }
  }
`;

export const CORE_EVENT_FIELDS = gql`
  fragment CoreEventFields on Event {
    event_id: id
    event_start: startISO8601
    event_end: endISO8601
    event_timestamp: timestamp
    event_number: type
    event_machine: machine
    event_device: device
    event_comment: comment
    event_user: user
  }
`;

export const CORE_LABEL_FIELDS = gql`
  fragment CoreLabelFields on Label {
    color
    name
    id
  }
`;

export const CORE_LOSSES_FIELDS = gql`
  fragment CoreLossesFields on Loss {
    average
    occurrences
    code
    value
    color
    name
    type
  }
`;

export const CORE_CYCLES_FIELDS = gql`
  fragment CoreCycleFields on Cycle {
    cycle_id: id
    cycle_start: start
    cycle_end: end
    cycle_timestamp: timestamp
    cycle_machine: machine
    cycle_device: device
    cycle_production: production
    duration
  }
`;

export const CYCLES_OUTPUTS_EXPAND_FIELDS = gql`
  fragment CycleOutputsExpandFields on Cycle {
    outputsExpand {
      id
      user
      date
      good
      reject
      machine
      device
      mo
      cycle
      changeover
    }
  }
`;

export const CORE_COMMAND_LAUNCH_FIELDS = gql`
  fragment CoreCommandLaunchFields on CommandLaunch {
    cl_id: id
    cl_machine: machine
    cl_device: device
    cl_device_command: deviceCommand
    cl_params: params
    cl_user: user
    cl_retry: retry
    cl_state: state
    updated_at: updatedAt
  }
`;

export const COMMAND_LAUNCH_DEVICE_SIGNATURE_FIELD = gql`
  fragment CommandLaunchDeviceSignatureField on CommandLaunch {
    cl_device_signature_expand: deviceSignatureExpand {
      ...CoreDeviceSignatureFields
    }
  }
`;

export const CORE_CHANGEOVER_FIELDS = gql`
  fragment CoreChangeoverFields on Changeover {
    changeover_id: id
    changeover_event: event
    changeover_mo: mo
    changeover_production: production
    changeover_updated_by: updatedBy
    changeover_parts_by_cycle: partsByCycle
    changeover_operations_by_cycle: operationsByCycle
    changeover_activate_counting: activateCounting
    changeover_cl_trigger: clTrigger
    changeover_phase: phase
    changeover_production_expand: productionExpand {
      production_id: id
      production_name: name
    }
    changeover_count_type_expand: countTypeExpand {
    ...CoreMachineCountTypeFields
    }
    is_last:isLast
    cycleReferenceTime: cycleReferenceTime
  }
`;

export const CHANGEOVER_MO_EXPAND_FIELD = gql`
  fragment ChangeoverMoExpandFields on Changeover {
    changeover_mo_expand: moExpand {
      mo_id: id
      mo_code: code
      mo_start: start
      mo_end: end
      mo_description: description
      mo_status: status
      mo_quantity: quantity
      mo_waste: waste
      mo_production: production
    }
  }
`;

export const CHANGEOVER_PRODUCTION_EXPAND_FIELD = gql`
  fragment ChangeoverProductionExpandFields on Changeover {
    changeover_production_expand: productionExpand {
      production_id: id
      production_name: name
    }
  }
`;

export const CORE_DEVICE_SIGNATURE_FIELDS = gql`
  fragment CoreDeviceSignatureFields on DeviceSignature {
    ds_id: id
    ds_parent_cl: parentCl
    ds_device: device
    ds_machine: machine
    ds_production: production
    ds_params: params
    updated_at: updatedAt
    ds_phase: phase
  }
`;

export const CORE_DEVICE_FIELDS = gql`
  fragment CoreDeviceFields on Device {
    device_id: id
    device_code: code
    device_name: name
    device_type: type
  }
`;

export const DEVICE_ADDITIONAL_INFO = gql`
  fragment DeviceAlertConfigurationFields on DeviceAlertConfiguration {
    id
    name
    threshold
    type
    version
  }
`;

export const EXTENDED_DEVICE_FIELDS = gql`
  fragment ExtendedDeviceFields on Device {
    device_id: id
    device_code: code
    device_description: description
    device_name: name
    device_machine: machine
    device_type: type
    device_serial_number: serialNumber
  }
`;

export const CORE_DEVICE_TYPE_FIELDS = gql`
  fragment CoreDeviceTypeFields on DeviceType {
    id
    code
    params
  }
`;

export const CORE_INDICATOR_FIELDS = gql`
  fragment CoreIndicatorFields on Indicator {
    indicator, rate, parts { name, value }, errors { message }
  }
`;

export const CORE_INDICATOR_EVOLUTION_FIELDS = gql`
  fragment CoreIndicatorEvolutionFields on IndicatorsEvolution {
    indicators { indicator, value, errors { message } }, start, end
  }
`;

export const CORE_JOB_PROGRESS_FIELDS = gql`
    fragment CoreJobProgressFields on JobProgress {
        delay
        ended
        estimated
        good
        overall_progress: overallProgress
        overall_delay: overallDelay
        reject
        started
        target
        theoretical @include(if: $withTheoretical)
    }
`;

export const CORE_CONSOLIDATED_OUTPUT_FIELDS = gql`
  fragment CoreConsolidatedOutputFields on ConsolidatedOutput {
    end
    good
    reject
    start
    theoretical
  }
`;

export const CORE_OUTPUT_FIELDS = gql`
  fragment CoreOutputFields on Output {
    date
    good
    reject
  }
`;

export const CORE_RUN_RATE_CHANGEOVER_FIELDS = gql`
  fragment CoreRunRateChangeoverFields on Changeover {
    runRate {
      actual
      max
      min
      theo
      timeScale
    }
  }
`;

export const CORE_RUN_RATE_FIELDS = gql`
  fragment CoreRunRateFields on ManufacturingOrder {
    runRate {
      actual
      max
      min
      theo
      timeScale
    }
  }
`;

export const CORE_EVENT_TYPE_FIELDS = gql`
    fragment CoreEventTypeFields on EventType {
        id
        code
        color
        parent
        type
        externalLink
        active
        notification
        customizable
        category
        requiredComment
        name
        names{
          fr
          en
          de
          es
          it
          pl
          zh
        }
        deletedAt
        description
        descriptions{
          fr
          en
          de
          es
          it
          pl
          zh
        }
        lastUse
    }
`;

export const EVENT_TYPE_PARENT_EXPAND_FIELDS = gql`
    fragment EventTypeParentExpandFields on EventType {
        parentExpand {
            id
            code
            color
            parent
            type
            externalLink
            active
            notification
            customizable
            category
            name
            names {
                fr
                en
                de
                es
                it
                pl
                zh
            }
            deletedAt
        }
    }
`;

export const EVENT_TYPE_FILTER_FIELDS = gql`
  fragment EventTypeFilterFields on EventType {
    factoriesExpand {
      id
      name
    }
    groupsExpand {
      id
      name
    }
    machineTypesExpand {
      id
      name
    }
    machinesExpand {
      id
      name
    }
  }
`;

export const ALL_SIMPLE_PAGINATOR_INFO_FIELDS = gql`
  fragment AllSimplePaginatorInfoFields on SimplePaginatorInfo {
    count
    firstItem
    hasMorePages
    lastItem
  }
`;

export const ALL_PAGINATOR_INFO_FIELDS = gql`
  fragment AllPaginatorInfoFields on PaginatorInfo {
    count
    firstItem
    lastItem
    lastPage
    total
  }
`;

export const CORE_NOTIFICATION_FIELDS = gql`
  fragment CoreNotificationFields on Notification {
    id
    start
    end
    eventExpand {
      event_id: id
      event_number_expand: typeExpand {
        code
        color
        id
#        name
          names {
              fr
              en
              de
              es
              it
              pl
              zh
          }
      }
      machineExpand {
        color
        id
        name
        machine_factory_expand: factoryExpand {
          factory_name:name
        }
      }
      deviceExpand {
        device_id: id
        device_code: code
      }
    }
  }
`;

export const CORE_MACHINE_TYPE_FIELDS = gql`
  fragment CoreMachineTypeFields on MachineType {
    id
    name
    defaultAssociatedCountType
  }
`;

export const CORE_PRODUCTION_FIELDS = gql`
  fragment CoreProductionFields on Production {
    production_id: id
    production_name: name
    description
#    descriptions{
#      en
#      fr
#      de
#    }
    imageUrl
    production_time: time
    isReferenced
    cycleReferenceTime
    machinesExpand {
      id
      name
    }
  }
`;

export const CORE_CLOSING_TIME_FIELDS = gql`
  fragment CoreClosingTimeFields on ClosingTime {
    ct_id: id
    ct_start: start
    ct_end: end
    title
    titles{
      en
      fr
      de
      es
      it
      pl
      zh
    }
  }
`;

// todo: lighten this fragment
export const CORE_OPENING_TIME_FIELDS = gql`
  fragment CoreOpeningTimeFields on OpeningTime {
    ot_id: id
    names{
      en
      fr
      de
      es
      it
      pl
      zh
    }
    name
    ot_is_default: isDefault
    ot_effective_date: effectiveDate
    isTemplate
    openingHours: openingHoursExpand {
      id
      day
      timeStart
      timeEnd
      enabled
      customized
      effectiveStartDate
      effectiveEndDate
      isNightShift
    }
    currentOpeningHours {
      id
      day
      timeStart
      timeEnd
      enabled
      customized
      effectiveStartDate
      effectiveEndDate
      isNightShift
    }
    openingHoursByPeriods {
      startDate
      endDate
      isCurrent
      openingHours {
        id
        day
        timeStart
        timeEnd
        enabled
        customized
        effectiveStartDate
        effectiveEndDate
        isNightShift
      }
    }
  }
`;

export const LIGHT_OPENING_TIME_FIELDS = gql`
    fragment LightOpeningTimeFields on OpeningTime {
        id
        name
        names {
            en
            fr
            de
            es
            it
            pl
            zh
        }
        isDefault
        effectiveDate
        isTemplate
        openingHoursByPeriods {
          startDate
          endDate
          isCurrent
          openingHours {
            id
            day
            timeStart
            timeEnd
            enabled
            customized
            effectiveStartDate
            effectiveEndDate
            isNightShift
          }
        }
    }`;

export const OPENING_TIME_CONFIGURATIONS_FIELDS = gql`
    fragment OpeningTimeConfigurationsFields on OpeningTime {
        openingTimeConfigurationsExpand {
            machineExpand {
                id
                name
            }
        }
    }
`;

export const CORE_MACHINE_OPENING_TIME_FIELDS = gql`
  fragment CoreMachineOpeningTimeFields on MachineOpeningTime {
    id
    name
    isDefault
    effectiveStartDate
    effectiveEndDate
    otType
    openingHoursExpand {
      id
      day
      timeStart
      timeEnd
      enabled
      customized
      effectiveStartDate
      effectiveEndDate
      isNightShift
    }
  }
`;

export const CORE_OPENING_HOUR_FIELDS = gql`
    fragment CoreOpeningHourFields on OpeningHour {
        id
        day
        timeStart
        timeEnd
        enabled
        customized
        effectiveStartDate
        effectiveEndDate
        isNightShift
    }
`;

export const CORE_DASHBOARD_MODULE_FIELDS = gql`
  fragment CoreDashboardModuleFields on DashboardModule {
    id
    x
    y
    w
    h
    i
    config
    title
  }
`;

export const CORE_DASHBOARD_FIELDS = gql`
  fragment CoreDashboardFields on Dashboard {
    id
    grid
    title
    createdAt
    categoryExpand {
      dc_id:id
      dc_name:name
      dc_translations: translations{
        en
        fr
      }
    }
  }
`;

export const CORE_FACTORY_FIELDS = gql`
  fragment CoreFactoryFields on Factory {
    id
    name
    shortCode
    country
    address
    city
    zipCode
    phone
    description
    color
    timezone
    imageUrl
    isDeletable
    planningOrder
    firstDayOfWeek
    startProductionTime
    startProductionDayOffset
    isDefault
  }
`;

export const EXTENDED_FACTORY_FIELDS = gql`
  fragment ExtendedFactoryFields on Factory {
    id
    name
    country
    address
    city
    zipCode
    phone
    description
    color
    shortCode
#    descriptions{
#      en
#      fr
#      de
#    }
    timezone
    imageUrl
    isDeletable
    planningOrder
    firstDayOfWeek
    startProductionTime
    startProductionDayOffset
    isDefault
    machinesExpand {
      id
      name
    }
    childrenExpand {
        data {
        id
        name
        color
        __typename
        }
        paginatorInfo {
        count
        total
        __typename
        }
        __typename
    }
    groupsExpand {
      id
      name
      machines: machinesExpand
      {
        id
        name
        factory
      }
    }
  }
`;

export const CORE_AREA_FIELDS = gql`
  fragment CoreAreaFields on Area {
    id
    name
    description
    descriptions {
        fr
        en
        de
        es
        it
        pl
        zh
    }
    color
    shortCode
    imageUrl
  }
`;

export const EXTENDED_AREA_FIELDS = gql`
  fragment ExtendedAreaFields on Area {
    id
    name
    description
    descriptions {
        fr
        en
        de
        es
        it
        pl
        zh
    }
    parentExpand {
      id
      name
      color
    }
    childrenExpand {
        data {
            id
            name
            color
            __typename
        }
        paginatorInfo {
            count
            total
            __typename
        }
        __typename
    }
    color
    imageUrl
    shortCode
    isDeletable
  }
`;

export const CORE_WORKCENTER_FIELDS = gql`
  fragment CoreWorkCenterFields on WorkCenter {
    id
    name
    description
    descriptions {
        fr
        en
        de
        es
        it
        pl
        zh
    }
    color
    imageUrl
    shortCode
  }
`;

export const EXTENDED_WORKCENTER_FIELDS = gql`
  fragment ExtendedWorkCenterFields on WorkCenter {
    id
    name
    shortCode
    description
    descriptions {
        fr
        en
        de
        es
        it
        pl
        zh
    }
    parentExpand {
      id
      name
      color
      parentExpand {
        id
      }
    }
    childrenExpand {
      data {
        id
        name
        color
      }
    }
    color
    imageUrl
    isDeletable
  }
`;

export const CORE_TIMEZONE_FIELDS = gql`
  fragment CoreTimezoneFields on TimeZone {
    name
  }
`;

export const CORE_AVERAGES_FIELDS = gql`
  fragment CoreAveragesFields on Averages {
    average_3_previous_jobs: averageThreePreviousJobs
    average_job: averageJob
    average_daily_job: averageDailyJob
  }
`;

export const CORE_MANUFACTURING_ORDER_FIELDS = gql`
  fragment CoreManufacturingOrderFields on ManufacturingOrder {
    id
    code
    start
    end
    expectedEnd
    description
    status
    origin
    quantity
    waste
    production
    factory
  }
`;

export const CORE_TEAM_FIELDS = gql`
  fragment CoreTeamFields on Team {
    id
    name
  }
`;

export const CORE_PHASE_FIELDS = gql`
  fragment CorePhaseFields on Phase {
    id
    number
    expectedStart
    expectedEnd
    preparationTime
    unitTime
    quantity
    status
    machine
    manufacturingOrder
    label
    preparationTimeRealized
    costCenter
    partsByCycle
    operationsByCycle
  }
`;

export const CORE_PHASE_REQUIRED_FIELDS = gql`
  fragment CorePhaseRequiredFields on Phase {
    id
    number
    status
    manufacturingOrder
  }
`;

export const CORE_RECURRING_EVENT_FIELDS = gql`
  fragment CoreRecurringEventFields on RecurringEvent {
    id
    title
    titles{
      fr
      en
      de
      es
      it
      pl
      zh
    }
    recurrenceStartDate
    recurrenceEndDate
    recurrencePattern
    occurrenceStartTime
    occurrenceEndTime
    occurrenceType
    occurrences
    type
  }
`;

export const EXTENDED_RECURRING_EVENT_FIELDS = gql`
  fragment ExtendedRecurringEventFields on RecurringEvent {
    machines
    occurrenceTypeExpand {
      id
      code
      name
      color
    }
  }
`;

export const SCOPE_DETAILS_RECURRING_EVENT_FIELDS = gql`
  fragment ScopeDetailsRecurringEventFields on RecurringEvent {
    machinesExpand {
      id
      name
      color
    }
  }
`;

export const CORE_DATA_EXPORT_REQUEST_FIELDS = gql`
  fragment CoreDataExportRequestFields on DataExportRequest {
    id
    status
    expiredAt
    source
    start
    end
    parameters
    createdAt
    title
    sanitize
    timezone
  }
`;

export const EXTENDED_DATA_EXPORT_REQUEST_FIELDS = gql`
  fragment ExtendedDataExportRequestFields on DataExportRequest {
    userExpand {
      fullName
    }
  }
`;

export const CORE_DATA_EXPORT_DOWNLOAD_LINK_FIELDS = gql`
  fragment CoreDataExportDownloadLinkFields on DataExportDownloadLink {
    url
    expiredAt
  }
`;

export const CORE_ALERT_FIELDS = gql`
fragment CoreAlertFields on Alert {
  id
  name
  active
  start
  end
  recipientsExpand {
    id
    userExpand {
      id
      email
      fullName
    }
  }
  conditionsExpand {
    id
    parameters
    alertType
  }
  machinesExpand {
    id
    name
    color
    devicesExpand  {
        alertConfigurationExpand {
            id
            name
        }
    }
  }
}

`;

export const CORE_COMPUTED_OPENING_HOUR_FIELDS = gql`
  fragment CoreComputedOpeningHourFields on ComputedOpeningHour {
    day
    times {
        timeStart
        timeEnd
    }
    enabled
  }
`;

export const CORE_DEVICE_ALERT_FIELDS = gql`
    fragment CoreDeviceAlertFields on DeviceAlert {
        id
        occurredAt
        device
        triggeringComputedMedian        
        machineExpand {
            id
            name
            groupsExpand {
                id
                name
            }
            factoryExpand {
                id
                name
            }
        }
        configurationExpand {
            id
            name
            type
            presetExpand {
              id
              name
            }
            threshold
        }
    }

`;

export const CORE_THEME_FIELDS = gql`
    fragment CoreThemeFields on Theme {
        id
        colors
        active
        internal
    }
`;

export const CORE_WIDGET_EXPORT_FIELDS = gql`
    fragment CoreWidgetExportFields on WidgetExport {
        id
        configuration
        viewCount
        createdBy
        viewLast
        createdAt
        updatedAt
        createdByExpand{
            fullName
        }
    }
`;

export const CORE_API_TOKEN_FIELDS = gql`
    fragment CoreApiTokenFields on ApiToken {
        id
        name
        tokenHint
        abilities
        scopes {
            type
            shortCode
            name
            color
        }
        expiresAt
        lastUsedAt
        createdAt
        createdBy
        status
        revokedAt
        revokedBy
    }
`;

export const CORE_TIMES_FIELDS = gql`
    fragment CoreTimesFields on TimeInterval {
        start
        end
        items {
            time
            value
            occurrences
            average
            color
        }
    }
`;

export const CORE_SCHEDULE_FIELDS = gql`
    fragment CoreScheduleFields on Schedule {
        id
        name
        description
        color
        validable
        shortCode
        isDeletable
        descriptions {
          fr
          en
          de
          es
          it
          pl
          zh
        }
    }
`;

export const SCHEDULE_SHIFT_FIELDS = gql`
    fragment ScheduleShiftFields on Shift {
      name
      shortCode
      color
      start
      end
      startMethod
      endMethod
      days
    }
`;
